<template>
  <auth-guard
    route
  >
    <url-breadcrumbs
      v-if="breadcrumbs"
      :items="breadcrumbs"
    />
    <div v-if="error">
      {{ error }}
    </div>
    <brokered-settlement-skeleton
      v-else-if="loading"
    />
    <v-card
      v-else
      class="pa-2 mx-auto"
      flat
      max-width="1000px"
    >
      <v-row>
        <v-col
          class="text-left"
          cols="3"
        >
          <v-img
            src="https://trackkat-static.s3.us-east-1.amazonaws.com/honeycomb-logo-full-black.png"
            :alt="config.name"
            height="100px"
            contain
          />
        </v-col>
        <v-col
          class="text-center mt-4"
          cols="6"
        >
          <div class="text-h4 font-weight-bold">
            Deposit Calculation
          </div>
          <div class="text-h6">
            {{ yardName }}
          </div>
        </v-col>
        <v-col
          class="text-right"
          cols="3"
        >
          <div class="mt-6">
            <div class="font-weight-medium">
              Date Shipped: {{ shipDate | formatDate }}
              <br>
              Lot: {{ lotName }}
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
        >
          <v-simple-table
            dense
          >
            <thead>
              <tr>
                <th>
                  Metal
                </th>
                <th>
                  Hedge ID
                </th>
                <th class="text-right">
                  Contract Return %
                </th>
                <th class="text-right">
                  Returnable Toz
                </th>
                <th class="text-right">
                  Hedge Value
                </th>
                <th class="text-right">
                  Total Value
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(alloc, index) in allocations.filter(a => +a.ptRtoz !== 0)"
                :key="'pt:' + index"
                class="text-right"
              >
                <td class="text-left">
                  <span v-if="index === 0">
                    Pt
                  </span>
                </td>
                <td class="text-left">
                  {{ alloc.hedgeId }}
                </td>
                <td />
                <td>
                  {{ alloc.ptRtoz }}
                </td>
                <td>
                  {{ alloc.ptMarketRate | currencyUSD }}
                </td>
                <td>
                  {{ alloc.ptValue | currencyUSD }}
                </td>
              </tr>
              <tr class="text-right font-weight-medium">
                <td />
                <td />
                <td>
                  {{ ptTermRate }} %
                </td>
                <td>
                  {{ allocatedReturnablePt }}
                </td>
                <td />
                <td>
                  {{ allocatedReturnablePtValue | currencyUSD }}
                </td>
              </tr>
              <tr><v-col colspan="6" /></tr>
              <tr
                v-for="(alloc, index) in allocations.filter(a => +a.pdRtoz !== 0)"
                :key="'pd:' + index"
                class="text-right"
              >
                <td class="text-left">
                  <span v-if="index === 0">
                    Pd
                  </span>
                </td>
                <td class="text-left">
                  {{ alloc.hedgeId }}
                </td>
                <td />
                <td>
                  {{ alloc.pdRtoz }}
                </td>
                <td>
                  {{ alloc.pdMarketRate | currencyUSD }}
                </td>
                <td>
                  {{ alloc.pdValue | currencyUSD }}
                </td>
              </tr>
              <tr class="text-right font-weight-medium">
                <td />
                <td />
                <td>
                  {{ pdTermRate }} %
                </td>
                <td>
                  {{ allocatedReturnablePd }}
                </td>
                <td />
                <td>
                  {{ allocatedReturnablePdValue | currencyUSD }}
                </td>
              </tr>
              <tr><v-col colspan="6" /></tr>
              <tr
                v-for="(alloc, index) in allocations.filter(a => +a.rhRtoz !== 0)"
                :key="'rh:' + index"
                class="text-right"
              >
                <td class="text-left">
                  <span v-if="index === 0">
                    Rh
                  </span>
                </td>
                <td class="text-left">
                  {{ alloc.hedgeId }}
                </td>
                <td />
                <td>
                  {{ alloc.rhRtoz }}
                </td>
                <td>
                  {{ alloc.rhMarketRate | currencyUSD }}
                </td>
                <td>
                  {{ alloc.rhValue | currencyUSD }}
                </td>
              </tr>
              <tr class="text-right font-weight-medium">
                <td />
                <td />
                <td>
                  {{ rhTermRate }} %
                </td>
                <td>
                  {{ allocatedReturnableRh }}
                </td>
                <td />
                <td>
                  {{ allocatedReturnableRhValue | currencyUSD }}
                </td>
              </tr>
              <tr><v-col colspan="6" /></tr>
              <tr>
                <td
                  class="text-right font-weight-bold"
                  colspan="5"
                >
                  Estimated Gross Value
                </td>
                <td class="text-right">
                  {{ grossMetalValue | currencyUSD }}
                </td>
              </tr>
              <tr v-if="+grossOutrightUnpaidValue > 0">
                <td
                  class="text-right font-weight-bold"
                  colspan="5"
                >
                  Gross Unpaid Unit Value (not shown)
                </td>
                <td class="text-right">
                  {{ grossOutrightUnpaidValue | currencyUSD }}
                </td>
              </tr>
              <tr v-if="!consolidateFees && +refiningFee > 0">
                <td
                  class="text-right font-weight-light"
                  colspan="5"
                >
                  Refining Fee
                </td>
                <td class="text-right">
                  ({{ refiningFee | currencyUSD }})
                </td>
              </tr>
              <tr v-if="!consolidateFees && +processingFee > 0">
                <td
                  class="text-right font-weight-light"
                  colspan="5"
                >
                  Processing Fee
                </td>
                <td class="text-right">
                  ({{ processingFee | currencyUSD }})
                </td>
              </tr>
              <tr v-if="!consolidateFees && +handlingFee > 0">
                <td
                  class="text-right font-weight-light"
                  colspan="5"
                >
                  Handling Fee
                </td>
                <td class="text-right">
                  ({{ handlingFee | currencyUSD }})
                </td>
              </tr>
              <tr v-if="!consolidateFees && +freightCharge > 0">
                <td
                  class="text-right font-weight-light"
                  colspan="5"
                >
                  Freight Charge
                </td>
                <td class="text-right">
                  ({{ freightCharge | currencyUSD }})
                </td>
              </tr>
              <tr v-if="!consolidateFees && +assayFee > 0">
                <td
                  class="text-right font-weight-light"
                  colspan="5"
                >
                  Assay Fee
                </td>
                <td class="text-right">
                  ({{ assayFee | currencyUSD }})
                </td>
              </tr>
              <tr v-if="!consolidateFees && +shreddingFee > 0">
                <td
                  class="text-right font-weight-light"
                  colspan="5"
                >
                  Shredding Fee
                </td>
                <td class="text-right">
                  ({{ shreddingFee | currencyUSD }})
                </td>
              </tr>
              <tr v-if="!consolidateFees && +netOtherFees > 0">
                <td
                  class="text-right font-weight-light"
                  colspan="5"
                >
                  Estimated Net Other Fees
                </td>
                <td class="text-right">
                  ({{ netOtherFees | currencyUSD }})
                </td>
              </tr>
              <tr>
                <td
                  class="text-right font-weight-bold"
                  colspan="5"
                >
                  Total Fees
                </td>
                <td class="text-right">
                  <span v-if="+totalFees > 0">
                    ({{ totalFees | currencyUSD }})
                  </span>
                  <span v-else>
                    {{ totalFees | currencyUSD }}
                  </span>
                </td>
              </tr>
              <tr>
                <td
                  class="text-right font-weight-bold"
                  colspan="5"
                >
                  Estimated Net Value
                </td>
                <td class="text-right">
                  {{ netValue | currencyUSD }}
                </td>
              </tr>
              <tr v-if="+brokerageFee > 0">
                <td
                  class="text-right font-weight-bold"
                  colspan="5"
                >
                  Estimated Brokerage Fee
                </td>
                <td class="text-right">
                  ({{ brokerageFee | currencyUSD }})
                </td>
              </tr>
              <tr v-if="+financeCharge > 0">
                <td
                  class="text-right font-weight-light"
                  colspan="5"
                >
                  Finance Charge
                </td>
                <td class="text-right">
                  ({{ financeCharge | currencyUSD }})
                </td>
              </tr>
              <tr v-if="!advances.length">
                <td
                  class="text-right font-weight-bold font-italic"
                  colspan="5"
                >
                  <info-tooltip>
                    Only example, no advance recorded
                  </info-tooltip>
                  Recommended Deposit at {{ depositPercent || 0 }}%
                </td>
                <td class="text-right font-weight-light font-italic">
                  ({{ depositTotal | currencyUSD }})
                </td>
              </tr>
              <tr
                v-else
                v-for="(advance, index) in advances"
                :key="index"
              >
                <td
                  class="text-right font-weight-bold"
                  colspan="5"
                >
                  <info-tooltip>
                    Advances shown in USD
                  </info-tooltip>
                  Advance {{ index += 1 }} - {{ Math.round((advance.amount_fx / netValue) * 100) }}%
                </td>
                <td class="text-right">
                  ({{ advance.amount_fx | currencyUSD }})
                  <br>
                  <span
                    v-if="$auth.user.fx_currency !== advance.currency"
                    class="text-caption font-italic"
                  >
                    {{ advance.currency }} ({{ advance.amount | currencyUSD }})
                  </span>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-col>
      </v-row>
    </v-card>
  </auth-guard>
</template>

<script>
import BrokeredSettlementSkeleton from '@/views/Lots/components/Brokered/components/BrokeredSettlementSkeleton.vue'
import config from '@/../izzi_config'

export default {
  name: 'AdvanceCalculatorPrint',
  components: {
    BrokeredSettlementSkeleton
  },
  data () {
    return {
      config,
      error: null,
      breadcrumbs: [
        ['lot list', { name: 'lot-list' }],
        [`Lot #${this.$route.params.id}`, { name: 'lot' }],
        [`Advanced Calculator`, { name: 'advance-calculator' }],
        ['Print', { name: 'advance-calculator-print' }]
      ],
      loading: false
    }
  },
  computed: {
    lotName () {
      return this.$store.getters['advancedCalculatorStore/getLotName']
    },
    lotId () {
      return this.$store.getters['advancedCalculatorStore/getLotId']
    },
    yardName () {
      const yardId = this.$store.getters['advancedCalculatorStore/getYardId']
      const yards = this.$store.getters['advancedCalculatorStore/getYardChoices']
      const yard = yards.find(y => y.id === yardId)
      return yard?.name
    },
    shipDate () {
      return this.$store.getters['advancedCalculatorStore/getShipDate']
    },
    convertersProcessed () {
      return this.$store.getters['advancedCalculatorStore/getConvertersProcessed']
    },
    mixedCatalystWeight () {
      return this.$store.getters['advancedCalculatorStore/getMixedCatalystWeight']
    },
    clientValueDate () {
      return this.$store.getters['advancedCalculatorStore/getClientValueDate']
    },
    refiningFee () {
      return this.$store.getters['advancedCalculatorStore/getRefiningFee']
    },
    processingFee () {
      return this.$store.getters['advancedCalculatorStore/getProcessingFee']
    },
    handlingFee () {
      return this.$store.getters['advancedCalculatorStore/getHandlingFee']
    },
    freightCharge () {
      return this.$store.getters['advancedCalculatorStore/getFreightCharge']
    },
    assayFee () {
      return this.$store.getters['advancedCalculatorStore/getAssayFee']
    },
    shreddingFee () {
      return this.$store.getters['advancedCalculatorStore/getShreddingFee']
    },
    brokerageFee () {
      return this.$store.getters['advancedCalculatorStore/getBrokerageFee']
    },
    netOtherFees () {
      return this.$store.getters['advancedCalculatorStore/getNetOtherFees']
    },
    totalFees () {
      return this.$store.getters['advancedCalculatorStore/getTotalFees']
    },
    consolidateFees () {
      return this.$store.getters['advancedCalculatorStore/getConsolidateFees']
    },
    netValue () {
      return this.$store.getters['advancedCalculatorStore/getNetValue']
    },
    settlementAmountDue () {
      return this.$store.getters['advancedCalculatorStore/getSettlementAmountDue']
    },
    finalSettlementAmountDue () {
      return this.$store.getters['advancedCalculatorStore/getFinalSettlementAmountDue']
    },
    expectedPaymentDate () {
      return this.$store.getters['advancedCalculatorStore/getExpectedPaymentDate']
    },
    interestPercent () {
      return this.$store.getters['advancedCalculatorStore/getInterestPercent']
    },
    financeCharge () {
      return this.$store.getters['advancedCalculatorStore/getFinanceCharge']
    },
    depositPercent () {
      return this.$store.getters['advancedCalculatorStore/getDepositPercent']
    },
    allocations () {
      return this.$store.getters['advancedCalculatorStore/getAllocations']
    },
    grossOutrightUnpaidValue () {
      return this.$store.getters['advancedCalculatorStore/getGrossOutrightUnpaidValue']
    },
    availablePt () {
      return this.$store.getters['advancedCalculatorStore/getAvailablePt']
    },
    availablePd () {
      return this.$store.getters['advancedCalculatorStore/getAvailablePd']
    },
    availableRh () {
      return this.$store.getters['advancedCalculatorStore/getAvailableRh']
    },
    grossMetalValue () {
      return this.$store.getters['advancedCalculatorStore/getGrossMetalValue']
    },
    depositTotal () {
      return this.$store.getters['advancedCalculatorStore/getDepositTotal']
    },
    brokerageTerms () {
      return this.$store.getters['advancedCalculatorStore/getCleanedBrokerageTerms']
    },
    ptTermRate () {
      return this.$store.getters['advancedCalculatorStore/getPtTermRate']
    },
    pdTermRate () {
      return this.$store.getters['advancedCalculatorStore/getPdTermRate']
    },
    rhTermRate () {
      return this.$store.getters['advancedCalculatorStore/getRhTermRate']
    },
    allocatedReturnablePt () {
      return this.$store.getters['advancedCalculatorStore/getAllocatedReturnablePt']
    },
    allocatedReturnablePd () {
      return this.$store.getters['advancedCalculatorStore/getAllocatedReturnablePd']
    },
    allocatedReturnableRh () {
      return this.$store.getters['advancedCalculatorStore/getAllocatedReturnableRh']
    },
    allocatedReturnablePtValue () {
      return this.$store.getters['advancedCalculatorStore/getAllocatedReturnablePtValue']
    },
    allocatedReturnablePdValue () {
      return this.$store.getters['advancedCalculatorStore/getAllocatedReturnablePdValue']
    },
    allocatedReturnableRhValue () {
      return this.$store.getters['advancedCalculatorStore/getAllocatedReturnableRhValue']
    },
    advances () {
      return this.$store.getters['advancedCalculatorStore/getAdvances']
    },
    history () {
      return this.$store.getters['advancedCalculatorStore/getAdvanceCalculatorHistory']
    }
  },
  methods: {
    async initialize () {
      return Promise.all([
        this.$store.dispatch('advancedCalculatorStore/fetchAdvanceCalculators'),
        this.$store.dispatch('advancedCalculatorStore/fetchDesignatedHedges'),
        this.$store.dispatch('advancedCalculatorStore/fetchAdvances')
      ])
    }
  },
  /*
   * Fetch the history, if no history then fetch the latest lot seed data
   */
  async mounted () {
    this.loading = true
    this.$store.commit('advancedCalculatorStore/clearState')
    this.$store.commit('advancedCalculatorStore/setLotId', this.$route.params.id)
    await this.initialize()
    if (this.history.length) {
      this.$store.commit('advancedCalculatorStore/setContextFromHistory', this.history[0])
    } else {
      // if there is no history, then seed data
      this.error = 'Unable to load a Advanced Calculator'
    }
    this.loading = false
  }
}
</script>
